import { isHexColor, memoOnce } from '@salsita-ai/platform-common/utils';
import { memoEval } from '@salsita-ai/platform-project-sdk/domain';
import { constants } from '../__generated/enhanced-model.js';
import { createRecipe, implementCustomExpressions, implementCustomInvariants } from '../__generated/implementation.js';
import { BaseCabinet, Countertop, EmptySpace, NoCabinet, TallCabinet24, TallCabinet36, WallCabinet } from '../__generated/model/index.js';
import { floorTextureSwatches } from '../data/floor-textures.js';
import { generateBom } from './bom-generator.js';
const INCH_TO_MM = 25.4;
const createCabinetRecipe = (type, offsetX)=>{
    return {
        title: `${constants[type].$name}`,
        recipes: [
            createRecipe({
                type,
                offsetX
            })
        ]
    };
};
const tallCabinetTypes = [
    'TallCabinet24',
    'TallCabinet36'
];
const createTallCabinetRecipes = (offsetX)=>{
    return tallCabinetTypes.map((type)=>createCabinetRecipe(type, offsetX));
};
const createWallCabinetRecipes = (offsetX)=>{
    return [
        createCabinetRecipe('WallCabinet', offsetX)
    ];
};
const createBaseCabinetWithCountertopRecipe = (type, offsetX)=>{
    return {
        title: `${constants[type].$name}`,
        recipes: [
            createRecipe({
                type,
                offsetX
            }),
            createRecipe({
                type: 'Countertop',
                offsetX
            })
        ]
    };
};
const baseCabinetTypes = [
    'BaseCabinet2Door',
    'BaseCabinet1Drawer2Doors',
    'BaseCabinet3Drawer',
    'BaseCabinet4Drawer'
];
const createBaseCabinetWithCountertopRecipes = (offsetX)=>{
    return baseCabinetTypes.map((type)=>createBaseCabinetWithCountertopRecipe(type, offsetX));
};
const createBaseCabinetWithoutCountertopRecipes = (offsetX)=>{
    return baseCabinetTypes.map((type)=>createCabinetRecipe(type, offsetX));
};
const createCountertopRecipes = (offsetX)=>{
    return [
        {
            title: 'Countertop',
            recipes: [
                createRecipe({
                    type: 'Countertop',
                    offsetX
                })
            ]
        }
    ];
};
const createEmptySpaceRecipes = (offsetX)=>{
    return [
        {
            title: 'Empty Space',
            recipes: [
                createRecipe({
                    type: 'EmptySpace',
                    offsetX
                })
            ]
        }
    ];
};
const createReplacementRecipesForBaseCabinet = (baseCabinet)=>{
    const recipes = [];
    baseCabinetTypes.forEach((type)=>{
        if (type !== baseCabinet.type) {
            recipes.push(createCabinetRecipe(type, baseCabinet.offsetX));
        }
    });
    if (baseCabinet.$noWallCabinetAbove) {
        recipes.push(...createTallCabinetRecipes(baseCabinet.offsetX));
    }
    recipes.push(...createCountertopRecipes(baseCabinet.offsetX));
    recipes.push(...createEmptySpaceRecipes(baseCabinet.offsetX));
    return recipes;
};
const createReplacementRecipesForTallCabinet = (tallCabinet)=>{
    const recipes = [];
    if (TallCabinet24.is(tallCabinet)) {
        recipes.push(createCabinetRecipe('TallCabinet36', tallCabinet.offsetX));
    }
    if (TallCabinet36.is(tallCabinet)) {
        recipes.push(createCabinetRecipe('TallCabinet24', tallCabinet.offsetX));
    }
    recipes.push(...createBaseCabinetWithoutCountertopRecipes(tallCabinet.offsetX));
    recipes.push(...createCountertopRecipes(tallCabinet.offsetX));
    recipes.push(...createEmptySpaceRecipes(tallCabinet.offsetX));
    return recipes;
};
const createReplacementRecipesForCountertop = (countertop)=>{
    if (!countertop.$isStandaloneCountertop) {
        return [];
    }
    return [
        ...createBaseCabinetWithoutCountertopRecipes(countertop.offsetX),
        ...countertop.$noWallCabinetAbove ? createTallCabinetRecipes(countertop.offsetX) : [],
        ...createEmptySpaceRecipes(countertop.offsetX)
    ];
};
const createReplacementRecipesForEmptySpace = (emptySpace)=>{
    return [
        ...createBaseCabinetWithoutCountertopRecipes(emptySpace.offsetX),
        ...emptySpace.$noWallCabinetAbove ? createTallCabinetRecipes(emptySpace.offsetX) : [],
        ...createCountertopRecipes(emptySpace.offsetX)
    ];
};
const createSwatchOptionMap = (materials)=>{
    const swatchOptionMap = {};
    for (const c of materials){
        swatchOptionMap[c.name] = {
            color: c.color && isHexColor(c.color) ? c.color : '#000000',
            swatchName: c.name,
            thumbnailUrl: c.swatchUrl ?? undefined
        };
    }
    return swatchOptionMap;
};
export const entityExpressions = implementCustomExpressions({
    RootEntity: {
        $colorSwatches: memoEval((_, __, ctx)=>[
                ctx.publishedData
            ], (publishedData)=>createSwatchOptionMap(publishedData?.cabinetMaterials ?? [])),
        $countertopColorSwatches: memoEval((_, __, ctx)=>[
                ctx.publishedData
            ], (publishedData)=>createSwatchOptionMap(publishedData?.countertopMaterials ?? [])),
        $segmentTotals: memoEval((self)=>[
                self.$segments
            ], (segments)=>segments.reduce((acc, s)=>({
                    minX: Math.min(acc.minX, s.offsetX * INCH_TO_MM),
                    maxX: Math.max(acc.maxX, s.offsetX * INCH_TO_MM + s.$width * INCH_TO_MM),
                    height: Math.max(acc.height, s.$height * INCH_TO_MM)
                }), {
                minX: 0,
                maxX: 0,
                height: 0
            })),
        $bom: memoEval((self, _, ctx)=>[
                self.$segments,
                ctx.publishedData,
                self.includeCountertop
            ], (segments, publishedData, includeCountertop)=>{
            return generateBom(segments, publishedData.segmentPrices, includeCountertop);
        }),
        $productGroups: memoEval((self)=>[
                self.$bom
            ], (lineItems)=>{
            const groups = new Map();
            for (const lineItem of lineItems){
                const code = lineItem.productCode;
                const existing = groups.get(code);
                if (existing) {
                    groups.set(code, {
                        ...existing,
                        count: existing.count + 1
                    });
                } else {
                    groups.set(code, {
                        productCode: lineItem.productCode,
                        productName: `${lineItem.segment.$name}${Countertop.is(lineItem.segment) ? ` (${lineItem.width}in)` : ''}`,
                        colorName: lineItem.segment.$colorName,
                        price: lineItem.price,
                        count: 1,
                        representativeEntity: lineItem.segment,
                        representativeOffsetX: lineItem.segment.offsetX
                    });
                }
            }
            return Array.from(groups.values());
        }),
        $price: memoEval((self)=>[
                self.$bom
            ], (lineItems)=>{
            return lineItems.reduce((acc, lineItem)=>acc + lineItem.price, 0);
        }),
        $isEmptyScene: memoEval((self)=>[
                self.$segments.length
            ], (length)=>length === 0),
        $boundingBoxMinX: memoEval((self)=>[
                self
            ], (self)=>{
            const totals = self.$segmentTotals;
            return totals.minX - 1000;
        }),
        $boundingBoxMaxX: memoEval((self)=>[
                self
            ], (self)=>{
            const totals = self.$segmentTotals;
            return totals.maxX + 1000;
        }),
        $boundingBoxMaxY: memoEval((self)=>[
                self
            ], (self)=>{
            const totals = self.$segmentTotals;
            return totals.height;
        }),
        $floorTextureSwatches: memoOnce(()=>floorTextureSwatches),
        $screenSizeAwareMaxDistance: memoEval((self)=>[
                self.$boundingBoxMaxX,
                self.$boundingBoxMinX
            ], (boundingBoxMaxX, boundingBoxMinX)=>{
            // Check if running in browser and determine screen size
            const isSmallScreen = typeof window !== 'undefined' && window.innerWidth < 1200;
            // Base values for small vs large screens
            const baseSmallScreen = 500;
            const baseLargeScreen = 250;
            // If bounding box is not available, return base values
            if (boundingBoxMaxX === undefined || boundingBoxMinX === undefined) {
                return isSmallScreen ? baseSmallScreen : baseLargeScreen;
            }
            // Calculate configuration width in mm
            const configWidth = boundingBoxMaxX - boundingBoxMinX;
            // Add extra distance for wider configurations (scale factor based on config width)
            // configWidth is in mm, add ~40 units per 1000mm of extra width beyond base
            const baseWidth = 3000 // Base configuration width (mm) - roughly 3 cabinets
            ;
            const extraWidth = Math.max(0, configWidth - baseWidth);
            const extraDistance = Math.floor(extraWidth / 1000) * 40;
            return isSmallScreen ? baseSmallScreen + extraDistance : baseLargeScreen + extraDistance;
        }),
        $initialBaseCabinetRecipes: memoOnce(()=>{
            return createBaseCabinetWithCountertopRecipes(0);
        }),
        $initialTallCabinetRecipes: memoOnce(()=>createTallCabinetRecipes(0)),
        $initialWallCabinetRecipes: memoOnce(()=>createWallCabinetRecipes(0))
    },
    Segment: {
        $tallCabinetRecipes: memoEval((self)=>[
                self.offsetX,
                self.$width
            ], (offsetX, width)=>createTallCabinetRecipes(offsetX + width)),
        $wallCabinetRecipes: memoEval((self)=>[
                self.offsetX,
                self.$width
            ], (offsetX, width)=>createWallCabinetRecipes(offsetX + width)),
        $wallCabinetAboveRecipes: memoEval((self)=>[
                self.offsetX
            ], (offsetX)=>createWallCabinetRecipes(offsetX)),
        $baseCabinetRecipes: memoEval((self)=>[
                self.offsetX,
                self.$width
            ], (offsetX, width)=>{
            return createBaseCabinetWithCountertopRecipes(offsetX + width);
        }),
        $noCabinetRecipes: memoEval((self)=>[
                self.offsetX,
                self.$width
            ], (offsetX, width)=>[
                ...createCountertopRecipes(offsetX + width),
                ...createEmptySpaceRecipes(offsetX + width)
            ]),
        $noCabinetLeftRecipes: memoEval((self)=>[
                self.offsetX
            ], (offsetX)=>[
                ...createCountertopRecipes(offsetX - 24),
                ...createEmptySpaceRecipes(offsetX - 24)
            ]),
        $emptySpaceRecipes: memoEval((self)=>[
                self.offsetX,
                self.$width
            ], (offsetX, width)=>createEmptySpaceRecipes(offsetX + width)),
        $emptySpaceLeftRecipes: memoEval((self)=>[
                self.offsetX
            ], (offsetX)=>createEmptySpaceRecipes(offsetX - 24)),
        $baseCabinetLeftRecipes: memoEval((self)=>[
                self.offsetX
            ], (offsetX)=>{
            return createBaseCabinetWithCountertopRecipes(offsetX - 24);
        }),
        $tallCabinetLeftRecipes: memoEval((self)=>[
                self.offsetX
            ], (offsetX)=>[
                createCabinetRecipe('TallCabinet24', offsetX - 24),
                createCabinetRecipe('TallCabinet36', offsetX - 36)
            ]),
        $wallCabinetLeftRecipes: memoEval((self)=>[
                self.offsetX
            ], (offsetX)=>createWallCabinetRecipes(offsetX - 24)),
        $fabCenteredOffsetX: memoEval((self)=>[
                self.$width
            ], (width)=>width / 2)
    },
    NonWallCabinetSegment: {
        $fabRightOffsetX: memoEval((self)=>[
                self.$width
            ], (width)=>width + 6)
    },
    Cabinet: {
        $isFirstSegment: memoEval((self)=>[
                self.offsetX,
                ...self.$scene.$segments.map((c)=>c.offsetX)
            ], (selfOffsetX, ...offsets)=>selfOffsetX === Math.min(...offsets)),
        $isLastSegment: memoEval((self)=>[
                self.offsetX,
                ...self.$scene.$segments.map((c)=>c.offsetX)
            ], (selfOffsetX, ...offsets)=>selfOffsetX === Math.max(...offsets)),
        $color: memoEval((self, _, ctx)=>[
                self,
                ctx.publishedData
            ], (self, publishedData)=>{
            if (self.$material !== self.$defaultMaterialName) return undefined;
            return publishedData?.cabinetMaterials.find((c)=>c.name === self.$colorName)?.color ?? undefined;
        }),
        $material: memoEval((self, _, ctx)=>[
                self,
                ctx.publishedData
            ], (self, publishedData)=>{
            return publishedData?.cabinetMaterials.find((c)=>c.name === self.$colorName)?.materialName ?? self.$defaultMaterialName;
        }),
        $hardwareMaterialColor: memoEval((self)=>[
                self.$scene.hardwareMaterial,
                self.$scene.$hardwareMaterialSwatches
            ], (hardwareMaterial, swatches)=>swatches[hardwareMaterial].color)
    },
    BaseCabinet: {
        $replacementRecipes: memoEval((self)=>[
                self
            ], (segment)=>createReplacementRecipesForBaseCabinet(segment)),
        $colorName: memoEval((self)=>[
                self.$scene.cabinetColor
            ], (cabinetColor)=>cabinetColor),
        $noWallCabinetAbove: memoEval((self)=>[
                self.offsetX,
                ...self.$scene.$segments.filter((c)=>WallCabinet.is(c)).map((c)=>c.offsetX)
            ], (selfOffsetX, ...wallOffsets)=>!wallOffsets.includes(selfOffsetX)),
        $matchingCountertop: memoEval((self)=>[
                self.offsetX,
                self.$scene.$segments
            ], (selfOffsetX, segments)=>{
            const countertops = segments.filter((s)=>Countertop.is(s));
            return countertops?.find((c)=>c.offsetX === selfOffsetX) ?? null;
        })
    },
    TallCabinet: {
        $replacementRecipes: memoEval((self)=>[
                self
            ], (segment)=>createReplacementRecipesForTallCabinet(segment)),
        $colorName: memoEval((self)=>[
                self.$scene.cabinetColor
            ], (cabinetColor)=>cabinetColor)
    },
    WallCabinet: {
        $colorName: memoEval((self)=>[
                self.$scene.hasDifferentWallCabinetColor,
                self.$scene.wallCabinetColor,
                self.$scene.cabinetColor
            ], (hasDifferent, wallColor, cabinetColor)=>hasDifferent ? wallColor : cabinetColor),
        $noBaseCabinetBelow: memoEval((self)=>[
                self.offsetX,
                ...self.$scene.$segments.filter((c)=>BaseCabinet.is(c) || NoCabinet.is(c)).map((c)=>c.offsetX)
            ], (selfOffsetX, ...baseCabinetOffsets)=>!baseCabinetOffsets.includes(selfOffsetX)),
        $baseCabinetBelowRecipes: memoEval((self)=>[
                self.offsetX
            ], (offsetX)=>createBaseCabinetWithCountertopRecipes(offsetX)),
        $noCabinetBelowRecipes: memoEval((self)=>[
                self.offsetX
            ], (offsetX)=>createCountertopRecipes(offsetX))
    },
    NoCabinet: {
        $noWallCabinetAbove: memoEval((self)=>[
                self.offsetX,
                ...self.$scene.$segments.filter((c)=>WallCabinet.is(c)).map((c)=>c.offsetX)
            ], (selfOffsetX, ...wallOffsets)=>!wallOffsets.includes(selfOffsetX))
    },
    Countertop: {
        $isIncluded: memoEval((self)=>[
                self.$scene.includeCountertop
            ], (includeCountertop)=>includeCountertop),
        $isExcluded: memoEval((self)=>[
                self.$scene.includeCountertop
            ], (includeCountertop)=>!includeCountertop),
        $replacementRecipes: memoEval((self)=>[
                self
            ], (segment)=>createReplacementRecipesForCountertop(segment)),
        $colorName: memoEval((self)=>[
                self.$scene.countertopColor
            ], (countertopColor)=>countertopColor),
        $isFirstSegment: memoEval((self)=>[
                self.$isStandaloneCountertop,
                self.offsetX,
                ...self.$scene.$segments.map((c)=>c.offsetX)
            ], (isStandaloneCountertop, selfOffsetX, ...offsets)=>{
            return isStandaloneCountertop === true && selfOffsetX === Math.min(...offsets);
        }),
        $isLastSegment: memoEval((self)=>[
                self.$isStandaloneCountertop,
                self.offsetX,
                ...self.$scene.$segments.map((c)=>c.offsetX)
            ], (isStandaloneCountertop, selfOffsetX, ...offsets)=>{
            return isStandaloneCountertop === true && selfOffsetX === Math.max(...offsets);
        }),
        $color: memoEval((self, _, ctx)=>[
                self.$scene.countertopColor,
                ctx.publishedData
            ], (countertopColor, publishedData)=>publishedData?.countertopMaterials.find((c)=>c.name === countertopColor)?.color ?? undefined),
        $material: memoEval((self, _, ctx)=>[
                self,
                self.$scene.countertopColor,
                ctx.publishedData
            ], (self, countertopColor, publishedData)=>{
            return publishedData?.countertopMaterials.find((c)=>c.name === countertopColor)?.materialName ?? self.$defaultMaterialName;
        }),
        $isStandaloneCountertop: memoEval((self)=>[
                self.offsetX,
                ...self.$scene.$segments.filter((c)=>BaseCabinet.is(c)).map((c)=>c.offsetX)
            ], (selfOffsetX, ...baseCabinetOffsets)=>!baseCabinetOffsets.includes(selfOffsetX)),
        $baseCabinetWithoutCountertopRecipes: memoEval((self)=>[
                self.offsetX
            ], (offsetX)=>{
            return createBaseCabinetWithoutCountertopRecipes(offsetX);
        })
    },
    EmptySpace: {
        $replacementRecipes: memoEval((self)=>[
                self
            ], (segment)=>createReplacementRecipesForEmptySpace(segment)),
        $colorName: ()=>'',
        $isFirstSegment: memoEval((self)=>[
                self.offsetX,
                ...self.$scene.$segments.map((c)=>c.offsetX)
            ], (selfOffsetX, ...offsets)=>selfOffsetX === Math.min(...offsets)),
        $isLastSegment: memoEval((self)=>[
                self.offsetX,
                ...self.$scene.$segments.map((c)=>c.offsetX)
            ], (selfOffsetX, ...offsets)=>selfOffsetX === Math.max(...offsets))
    }
});
export const entityInvariants = implementCustomInvariants({
    Scene: {
        noCabinetAtEnds: (scene)=>{
            const sortedSegments = [
                ...scene.$segments
            ].sort((a, b)=>a.offsetX - b.offsetX);
            const filteredSegments = sortedSegments.filter((s)=>!Countertop.is(s) || !sortedSegments.find((s2)=>s2 !== s && s2.offsetX === s.offsetX));
            const leftmostSegment = filteredSegments.length > 0 ? filteredSegments[0] : undefined;
            const rightmostSegment = filteredSegments.length > 0 ? filteredSegments[filteredSegments.length - 1] : undefined;
            if (!leftmostSegment || !rightmostSegment) {
                return true;
            }
            return !NoCabinet.is(leftmostSegment) && !NoCabinet.is(rightmostSegment);
        },
        openSpaceMaxLength: (scene)=>{
            const openSpaces = scene.$segments.filter((s)=>EmptySpace.is(s) || Countertop.is(s) && s.$isStandaloneCountertop).sort((a, b)=>a.offsetX - b.offsetX);
            const acc = openSpaces.reduce(({ maxLength, currentLength, previous }, currentSegment)=>{
                if (!previous) {
                    return {
                        maxLength: currentSegment.$width,
                        currentLength: currentSegment.$width,
                        previous: currentSegment
                    };
                } else if (previous.offsetX + previous.$width === currentSegment.offsetX) {
                    const newCurrentLength = currentLength + currentSegment.$width;
                    if (newCurrentLength > maxLength) {
                        return {
                            maxLength: newCurrentLength,
                            currentLength: newCurrentLength,
                            previous: currentSegment
                        };
                    } else {
                        return {
                            maxLength,
                            currentLength: newCurrentLength,
                            previous: currentSegment
                        };
                    }
                } else {
                    return {
                        maxLength,
                        currentLength: currentSegment.$width,
                        previous: currentSegment
                    };
                }
            }, {
                maxLength: 0,
                currentLength: 0,
                previous: undefined
            });
            return acc.maxLength <= 48;
        },
        noOpenSpaceNextToCountertop: (scene)=>{
            const sortedSegments = [
                ...scene.$segments
            ].sort((a, b)=>a.offsetX - b.offsetX);
            return !sortedSegments.some((segment, index)=>{
                const previousSegment = sortedSegments[index - 1];
                const nextSegment = sortedSegments[index + 1];
                if (Countertop.is(segment) && segment.$isStandaloneCountertop) {
                    if (previousSegment && EmptySpace.is(previousSegment)) {
                        return true;
                    }
                    if (nextSegment && EmptySpace.is(nextSegment)) {
                        return true;
                    }
                }
                return false;
            });
        },
        countertopSectionMaxLength: (scene)=>{
            const countertops = scene.$segments.filter((s)=>Countertop.is(s)).sort((a, b)=>a.offsetX - b.offsetX);
            let currentLength = 0;
            let offsetX = 0;
            for (const countertop of countertops){
                if (countertop.offsetX !== offsetX) {
                    currentLength = 0;
                }
                currentLength += countertop.$width;
                offsetX = countertop.offsetX + countertop.$width;
                if (currentLength > 192) {
                    return false;
                }
            }
            return true;
        },
        totalConfigurationMaxLength: (scene)=>{
            const acc = scene.$segments.reduce((acc, segment)=>{
                return {
                    minX: Math.min(acc.minX, segment.offsetX),
                    maxX: Math.max(acc.maxX, segment.offsetX + segment.$width)
                };
            }, {
                minX: 0,
                maxX: 0
            });
            return acc.maxX - acc.minX <= 480;
        }
    }
});

//# sourceMappingURL=expressions.js.map